import { forwardRef, type InputHTMLAttributes } from "react";
import { cn } from "../cn";

/* Native range input styled with the DiscordForge accent. discordforge.org
 * uses native sliders with accent-color on range inputs; we keep the native
 * behavior and add orange theming + a visible filled track. */
export interface SliderProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type" | "size"> {
    /** Show the current value next to the track (right-aligned) */
    showValue?: boolean;
}

export const Slider = forwardRef<HTMLInputElement, SliderProps>(
    ({ className, showValue = false, value, ...rest }, ref) => (
        <div className={cn("flex items-center gap-3", className)}>
            <input
                ref={ref}
                type="range"
                value={value}
                className={cn(
                    "h-1.5 w-full cursor-pointer appearance-none rounded-full bg-white/10 outline-none",
                    "focus-visible:ring-2 focus-visible:ring-orange-400/50",
                    "disabled:cursor-not-allowed disabled:opacity-50",
                    // Firefox
                    "[&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:appearance-none",
                    "[&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-[#f97316]",
                    "[&::-moz-range-thumb]:shadow-[0_0_12px_rgba(249,115,22,0.5)] [&::-moz-range-thumb]:transition-transform",
                    // WebKit
                    "[&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:appearance-none",
                    "[&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-0 [&::-webkit-slider-thumb]:bg-[#f97316]",
                    "[&::-webkit-slider-thumb]:shadow-[0_0_12px_rgba(249,115,22,0.5)] [&::-webkit-slider-thumb]:transition-transform"
                )}
                {...rest}
            />
            {showValue && (
                <span className="w-10 shrink-0 text-right text-xs font-bold tabular-nums text-zinc-300">
                    {String(value)}
                </span>
            )}
        </div>
    )
);

Slider.displayName = "Slider";
